// @ts-nocheck
/* eslint-disable @typescript-eslint/naming-convention */
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    BillToModel,
    BrandModel,
    CartLineModel,
    CartModel,
    OrderLineModel,
    OrderModel,
    ProductModel,
    RmaLineDto,
    RmaModel,
} from "@insite/client-framework/Types/ApiModels";
import { breadcrumbs } from "@store/Themes/common/styleguide";
import useCurrency from "@store/Framework/Common/Hooks/useCurrency";

declare global {
    interface Window {
        dataLayer?: [{}];
    }
}

const windowExist = typeof window !== "undefined";

export const push = (data: object) => {
    if (windowExist && Object.prototype.hasOwnProperty.call(window, "dataLayer")) {
        window.dataLayer?.push({ ecommerce: null });
        window.dataLayer?.push(data);
    }
    // Use this locally
    //  else {
    //     global.console.log(data);
    // }
};

export const resolveCategory = (itemUrlSegment: string) => {
    if (windowExist) {
        const resolvedCategory = window.location.pathname
            .slice(1)
            .replace(/-/gu, " ")
            .split("/")
            .filter((category, i) => i !== 0);
        const resolvedCat = resolvedCategory.join("/");
        const itemSegment = `/${itemUrlSegment}`;
        const strippedCat = resolvedCat.substring(0, resolvedCat.length - itemSegment.length);
        return resolvedCat.includes(itemUrlSegment) ? strippedCat : resolvedCat;
    }
};

export const resolveVariant = (title: any) => title.replace(/[sS]*.*( - )/, "");

const formatPrice = (price: number) => price?.toFixed(2) ?? "";

const itemOutput = (
    index: number,
    item: any,
    currency: string | undefined,
    productInfo?: ProductInfo | undefined,
    forcedQuantity?: number | undefined,
): object => {
    const priceModelCheck = productInfo
        ? productInfo.pricing?.unitNetPrice ?? 0
        : item
        ? item.pricing?.unitNetPrice
        : 0;
    const priceModel = item?.unitListPrice ? item.unitListPrice : priceModelCheck;

    let brand = item ? (item.brand ? item.brand : "") : "";
    if (typeof brand !== "string" && item.brand?.name) {
        brand = item.brand?.name;
    }
    const item_id = item ? (item.productNumber ? item.productNumber : item.productId ? item.productId : "") : "";
    const item_name = item
        ? item.productTitle
            ? item.productTitle
            : item.shortDescription
            ? item.shortDescription
            : ""
        : "";
    const item_urlSegment = item ? item.urlSegment : "";
    const quantityOutput = forcedQuantity || (productInfo ? productInfo.qtyOrdered : item ? item.qtyOrdered : 0);
    const quantity = quantityOutput || 1;

    return {
        item_id,
        item_name,
        affiliation: "",
        coupon: "",
        currency,
        discount: 0,
        index,
        item_brand: brand || "",
        item_category: resolveCategory(item_urlSegment),
        item_category2: "",
        item_list_id: "",
        item_list_name: "",
        item_variant: "", // item.variant ? item.variant : "", not implemented yet LEVIIN-68
        location_id: "",
        price: formatPrice(priceModel),
        quantity,
    };
};

const mapCartLines = (cartLines: CartLineModel[], currency: string | undefined) => {
    return cartLines?.map((cartLine, index) => {
        const item = {
            ...cartLine,
            productTitle: cartLine.shortDescription,
            productNumber: cartLine.erpNumber,
            variant: resolveVariant(cartLine.shortDescription),
        };
        return itemOutput(index + 1, item, currency);
    });
};

const mapProductImpression = (items: ProductModel[] | undefined, currency: string | undefined) => {
    return items?.map((item, index) => {
        return itemOutput(index + 1, item, currency);
    });
};

const mapCartLinesFromCartModel = (cart: any, currency: string | undefined) => {
    const cartLines: CartLineModel[] = cart.cartLines;
    return mapCartLines(cartLines, currency);
};

const mapProductContext = (
    product: ProductModel | undefined,
    currency: string | undefined,
    productInfo: ProductInfo | undefined,
): object => {
    return itemOutput(0, product, currency, productInfo);
};

const mapCartLine = (cartLine: CartLineModel, currency: string | undefined, forcedQuantity?: number): object => {
    return itemOutput(0, cartLine, currency, undefined, forcedQuantity);
};

const mapOrderLineModel = (
    orderLine: OrderLineModel | ProductModel,
    currency: string | undefined,
    forcedQuantity?: number,
): object => {
    return itemOutput(0, orderLine, currency, undefined, forcedQuantity);
};

const isCartLine = function (
    productObject: CartLineModel | ProductContextModel | OrderLineModel | ProductModel,
): productObject is CartLineModel {
    return (productObject as CartLineModel).line !== undefined;
};

const isProductContext = function (
    productObject: CartLineModel | ProductContextModel | OrderLineModel | ProductModel,
): productObject is ProductContextModel {
    return (
        (productObject as CartLineModel).line === undefined &&
        (productObject as OrderLineModel).lineNumber === undefined
    );
};

const isOrderLine = function (
    productObject: CartLineModel | ProductContextModel | OrderLineModel | ProductModel,
): productObject is ProductContextModel {
    return (productObject as OrderLineModel).lineNumber !== undefined;
};

// view_item
export const dispatchProductDetailView = (
    product: ProductModel | undefined,
    currency: string | undefined,
    productInfo: ProductInfo | undefined,
) => {
    push({
        event: "view_item",
        ecommerce: {
            items: mapProductContext(product, currency, productInfo),
        },
    });
};

// view_item_list
export const dispatchProductImpressions = (
    items: ProductModel[] | undefined,
    currency: string | undefined,
    pageType: string,
) => {
    if (items?.length === 0) {
        return;
    }

    push({
        event: "view_item_list",
        ecommerce: {
            items: mapProductImpression(items, currency),
        },
    });
};

// add_to_cart
export const dispatchAddToCartEvent = (productContext: ProductContextModel, currency: string | undefined) => {
    const { product, productInfo } = productContext;
    push({
        event: "add_to_cart",
        ecommerce: {
            items: mapProductContext(product, currency, productInfo),
        },
    });
};

// remove_from_cart (single)
export const dispatchRemoveFromCartEvent = (cartLine: CartLineModel, currency: string | undefined) => {
    push({
        event: "remove_from_cart",
        ecommerce: {
            items: mapCartLine(cartLine, currency),
        },
    });
};

// remove_from_cart (empty cart)
export const dispatchEmptyCartEvent = () => {
    push({
        event: "remove_from_cart",
    });
};

// view_cart
export const dispatchViewCartEvent = (cart: CartModel | undefined, currency: string | undefined) => {
    const value = cart ? cart.orderGrandTotal : "0.00";
    const items = cart ? mapCartLinesFromCartModel(cart, currency) : [];
    if (items && items.length > 0) {
        push({
            event: "view_cart",
            ecommerce: {
                currency,
                value,
                items,
            },
        });
    }
};

// begin_checkout
export const dispatchCheckoutEvent = (
    stepNumber: number,
    cart: CartModel,
    currency: string | undefined,
    optionString?: string,
) => {
    const actionField: any = { step: stepNumber, action: "checkout" };

    if (optionString) {
        actionField.option = optionString;
    }
    push({
        event: "begin_checkout",
        ecommerce: {
            items: mapCartLinesFromCartModel(cart, currency) ?? [],
        },
    });
};

// add_payment_info
export const dispatchPaymentInfoEvent = (cart: CartModel, currency: string | undefined, coupon?: string) => {
    push({
        event: "add_payment_info",
        ecommerce: {
            currency,
            value: formatPrice(cart.orderSubTotal),
            coupon: cart.promotionCode ? cart.promotionCode : coupon,
            payment_type: cart.paymentMethod, // use the type of payment selected by the user.
            items: mapCartLinesFromCartModel(cart, currency) ?? [],
        },
    });
};

// add_shipping_info
export const dispatchShippingInfoEvent = (cart: CartModel, currency: string | undefined, coupon?: string) => {
    push({
        event: "add_shipping_info",
        ecommerce: {
            currency,
            value: formatPrice(cart.orderSubTotal),
            coupon: cart.promotionCode ? cart.promotionCode : coupon,
            shipping_tier: cart.shipVia,
            items: mapCartLinesFromCartModel(cart, currency) ?? [],
        },
    });
};

// purchase
export const dispatchPurchaseEvent = (
    orderNumber: string,
    cart: CartModel,
    billTo?: BillToModel,
    coupon?: string,
    currency?: string,
) => {
    push({
        event: "purchase",
        ecommerce: {
            transaction_id: orderNumber,
            affiliation: billTo?.customerName ?? "Online Store",
            value: formatPrice(cart.orderSubTotal),
            tax: formatPrice(cart.totalTax),
            shipping: formatPrice(cart.shippingAndHandling),
            coupon: cart.promotionCode ? cart.promotionCode : coupon,
            currency,
            items: mapCartLinesFromCartModel(cart, currency) ?? [],
        },
    });
};

// select_item
export const dispatchProductClickEvent = (
    productObject: CartLineModel | ProductContextModel | OrderLineModel | ProductModel,
    currency: string | undefined,
) => {
    let theProduct;
    if (isCartLine(productObject)) {
        theProduct = mapCartLine(productObject, currency);
    } else if (isProductContext(productObject)) {
        const { product, productInfo } = productObject;
        theProduct = mapProductContext(product, currency, productInfo);
    } else if (isOrderLine(productObject)) {
        theProduct = mapOrderLineModel(productObject, currency);
    }
    push({
        event: "select_item",
        ecommerce: {
            items: [theProduct],
        },
    });
};

// Add/Remove Line Cart Event
export const dispatchCartLineQuantityChangeEvent = (
    cartLine: CartLineModel,
    newQty: number,
    oldQty: number,
    currency: string | undefined,
) => {
    if (newQty > oldQty) {
        push({
            event: "add_to_cart",
            ecommerce: {
                items: [mapCartLine(cartLine, currency, newQty - oldQty)],
            },
        });
    } else {
        push({
            event: "remove_from_cart",
            ecommerce: {
                items: [mapCartLine(cartLine, currency, oldQty - newQty)],
            },
        });
    }
};
